<template>
	<n-modal v-model:show="showSearchBox" class="search-box-modal">
		<Card
			content-class="p-0!"
			class="w-150!"
			size="large"
			role="dialog"
			aria-modal="true"
			style="position: fixed; right: 50%; transform: translateX(50%); top: -20px"
		>
			<div class="search-box" @keydown.up="prevItem()" @keydown.down="nextItem()">
				<div class="search-input flex items-center">
					<Icon :name="SEARCH_ICONS.search" :size="16" />
					<input v-model="search" :placeholder="$t(`common.misc.search`)" class="grow" />
					<n-text v-if="showLabels" code>ESC</n-text>
					<Icon :name="SEARCH_ICONS.close" :size="20" class="cursor-pointer" @click="closeBox()" />
				</div>
				<n-divider />
				<n-scrollbar ref="scrollContent" class="h-96!">
					<div class="conten-wrap">
						<div v-for="group of filteredGroups" :key="group.name" class="group">
							<div class="group-title">{{ group.name }}</div>
							<n-spin :show="group.loading">
								<div class="group-list">
									<button
										v-for="item of group.items"
										:id="item.key.toString()"
										:key="item.key"
										class="item flex items-center"
										:class="{ active: item.key === activeItem }"
										@click="callAction(item.action)"
									>
										<div class="icon shrink-0">
											<n-avatar
												v-if="item.iconImage"
												round
												:size="28"
												:src="item.iconImage"
												:img-props="{ alt: 'avatar' }"
											/>
											<Icon v-if="item.iconName" :name="item.iconName" :size="16" />
										</div>
										<div class="title grow">
											<Highlighter
												highlight-class-name="highlight"
												:search-words="keywords"
												auto-escape
												:text-to-highlight="item.title"
											/>
										</div>
										<div class="label text-right" :class="{ hidden: !showLabels }">
											{{ item.label }}
										</div>
									</button>
								</div>
							</n-spin>
						</div>
						<div v-if="!filteredGroups.length" class="group-empty">
							{{ $t(`common.search.no_results_message`, { search }) }}
						</div>
					</div>
				</n-scrollbar>
				<n-divider />
				<div class="hint-bar flex items-center justify-center">
					<div class="hint flex items-center justify-center gap-1">
						<div class="icon">
							<Icon :name="SEARCH_ICONS.arrowEnter" :size="12" />
						</div>
						<span class="label">{{ $t(`common.misc.select`) }}</span>
					</div>
					<div class="hint flex items-center justify-center gap-1">
						<div class="icon">
							<Icon :name="SEARCH_ICONS.arrowSort" :size="12" />
						</div>
						<span class="label">{{ $t(`common.misc.navigate`) }}</span>
					</div>
				</div>
			</div>
		</Card>
	</n-modal>
</template>

<script lang="ts" setup>
import type { ScrollbarInst } from "naive-ui"
import type { RouteRecordRaw } from "vue-router"
import type { I18nString } from "@/lang/config"
import { useMagicKeys, watchDebounced, whenever } from "@vueuse/core"
import { NAvatar, NDivider, NModal, NScrollbar, NSpin, NText } from "naive-ui"
import { computed, onMounted, ref } from "vue"
import Highlighter from "vue-highlight-words"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"
import Api from "@/api"
import Card from "@/components/common/Card.vue"
import Icon from "@/components/common/Icon.vue"
import { useAppointmentForm } from "@/composables/calendar/useAppointmentForm"
import { useFullscreenSwitch } from "@/composables/common/useFullscreenSwitch"
import { useSearchDialog } from "@/composables/common/useSearchDialog"
import { useNavigation } from "@/composables/router/useNavigation"
import { ICONS } from "@/const"
import { getOS } from "@/utils"

interface GroupItem {
	iconName: string | null
	iconImage: string | null
	key: number | string
	title: string
	label: string
	tags?: string[]
	action: () => void
}

interface Group {
	name: string
	items: GroupItem[]
	loading: boolean
}

type Groups = Group[]

interface RouteFilter {
	type: "exclude" | "include"
	names: string[]
}

defineProps<{
	showLabels?: boolean
}>()

const SEARCH_ICONS = {
	search: "ion:search-outline",
	close: "ion:close",
	arrowEnter: "fluent:arrow-enter-left-24-regular",
	arrowSort: "fluent:arrow-sort-24-regular",
	fullscreen: "fluent:full-screen-maximize-24-regular",
	patient: ICONS.pages.patients,
	document: "carbon:document",
	addPatient: "carbon:face-add",
	newAppointment: "mdi:calendar-plus",
	calculator: ICONS.tools.pregnancy_calculator,
	monitoring: ICONS.tools.monitoring,
	biometry: ICONS.tools.biometry
} as const

const INCLUDED_ROUTES = ["Dashboard", "Calendar", "PatientsList", "PatientCreate", "Profile"] as const
const MULTIPLE_SLASHES_REGEX = /\/+/g
const TRAILING_SLASH_REGEX = /\/$/

const { t } = useI18n()
const router = useRouter()
const { routePatients, routePregnancyCalculator, routeMonitoring, routeBiometry } = useNavigation()
const showSearchBox = ref(false)
const search = ref("")
const activeItem = ref<null | string | number>(null)
const scrollContent = ref<(ScrollbarInst & { $el: HTMLElement }) | null>(null)

function createItem(
	key: number | string,
	title: string,
	label: string,
	action: () => void,
	iconName: string | null = null,
	iconImage: string | null = null
): GroupItem {
	return {
		iconName,
		iconImage,
		key,
		title,
		label,
		action
	}
}

const ApplicationsGroup = ref<Group>({
	name: t<I18nString>(`common.search.tools`),
	items: [
		/*
		createItem(
			1,
			t<I18nString>(`common.search.add_new_patient`),
			t<I18nString>(`common.search.shortcut`),
			() => routePatients({ patientId: "create" }).navigate(),
			ICONS.addPatient
		),
		*/
		createItem(
			5,
			t<I18nString>(`common.search.new_appointment`),
			t<I18nString>(`common.search.shortcut`),
			() => useAppointmentForm().open(),
			SEARCH_ICONS.newAppointment
		),
		createItem(
			2,
			t<I18nString>(`common.search.pregnancy_calculator`),
			t<I18nString>(`common.search.shortcut`),
			() => routePregnancyCalculator().navigate(),
			SEARCH_ICONS.calculator
		),
		createItem(
			3,
			t<I18nString>(`common.search.monitoring`),
			t<I18nString>(`common.search.shortcut`),
			() => routeMonitoring().navigate(),
			SEARCH_ICONS.monitoring
		),
		createItem(
			4,
			t<I18nString>(`common.search.biometry`),
			t<I18nString>(`common.search.shortcut`),
			() => routeBiometry().navigate(),
			SEARCH_ICONS.biometry
		)
	],
	loading: false
})

const PatientsGroup = ref<Group>({
	name: t<I18nString>(`common.search.patients`),
	items: [],
	loading: false
})

const ActionsGroup = ref<Group>({
	name: t<I18nString>(`common.search.actions`),
	items: [
		createItem(
			7,
			t<I18nString>(`common.search.toggle_fullscreen`),
			t<I18nString>(`common.search.action`),
			() => useFullscreenSwitch().toggle(),
			SEARCH_ICONS.fullscreen
		)
	],
	loading: false
})

function buildFullPath(route: RouteRecordRaw, parentPath: string): string {
	if (route.path.startsWith("/")) return route.path
	if (route.path === "") return parentPath
	return parentPath ? `${parentPath}/${route.path}` : `/${route.path}`
}

function normalizePath(path: string): string {
	return path.replace(MULTIPLE_SLASHES_REGEX, "/").replace(TRAILING_SLASH_REGEX, "") || "/"
}

function shouldIncludeRoute(routeName: string, filter?: RouteFilter): boolean {
	if (!filter) return true
	return filter.type === "exclude" ? !filter.names.includes(routeName) : filter.names.includes(routeName)
}

function getAllRoutes(
	routeList: RouteRecordRaw[],
	parentPath = "",
	filter?: RouteFilter
): Array<RouteRecordRaw & { fullPath: string }> {
	const routes: Array<RouteRecordRaw & { fullPath: string }> = []

	for (const route of routeList) {
		const fullPath = normalizePath(buildFullPath(route, parentPath))

		if (route.name && route.meta?.title && shouldIncludeRoute(route.name as string, filter)) {
			routes.push({ ...route, fullPath })
		}

		if (route.children) {
			routes.push(...getAllRoutes(route.children, fullPath, filter))
		}
	}

	return routes
}

const PagesGroup = computed<Group>(() => {
	const allRoutes = getAllRoutes(router.getRoutes(), "", {
		type: "include",
		names: [...INCLUDED_ROUTES]
	})

	const items: GroupItem[] = allRoutes.map((route, index) => {
		const routeName = route.name as string
		const titleKey = `navigation.titles.${String(route.meta?.title)}` as I18nString
		const title = t<I18nString>(titleKey, routeName || route.path)

		return {
			iconName: SEARCH_ICONS.document,
			iconImage: null,
			key: `page-${routeName}-${index}`,
			title,
			label: route.fullPath,
			action: () => router.push({ name: route.name })
		}
	})

	return {
		name: t<I18nString>(`common.search.pages`),
		items,
		loading: false
	}
})

const groups = computed<Groups>(() => [
	ApplicationsGroup.value,
	PatientsGroup.value,
	PagesGroup.value,
	ActionsGroup.value
])

const keywords = computed<string[]>(() => (search.value.length > 1 ? search.value.split(" ").filter(Boolean) : []))

function matchesKeyword(text: string, keywords: string[]): boolean {
	return keywords.some(k => text.toLowerCase().includes(k.toLowerCase()))
}

const filteredGroups = computed<Groups>(() => {
	if (!keywords.value.length) {
		return groups.value.filter(group => group.items.length || group.loading)
	}

	return groups.value
		.map(group => ({
			name: group.name,
			items: group.items.filter(
				item =>
					matchesKeyword(item.title, keywords.value) ||
					item.tags?.some(tag => matchesKeyword(tag, keywords.value))
			),
			loading: group.loading
		}))
		.filter(group => group.items.length || group.loading)
})

const filteredFlattenItems = computed<GroupItem[]>(() => filteredGroups.value.flatMap(group => group.items))

function resetSearch() {
	search.value = ""
	activeItem.value = null
}

function openBox(e?: MouseEvent) {
	if (!showSearchBox.value) {
		showSearchBox.value = true
		getPatientsList()
		setTimeout(resetSearch, 100)
	}
	return e
}

function closeBox() {
	showSearchBox.value = false
	resetSearch()
}

function callAction(action: () => void) {
	action()
	closeBox()
}

function navigateItem(direction: "next" | "prev") {
	const items = filteredFlattenItems.value
	if (!items.length) return

	const currentIndex = items.findIndex(item => item.key === activeItem.value)
	const isAtEnd = currentIndex === items.length - 1
	const isAtStart = currentIndex === 0
	const nextItem = items[currentIndex + 1]
	const prevItem = items[currentIndex - 1]
	const firstItem = items[0]
	const lastItem = items.at(-1)

	if (direction === "next") {
		activeItem.value = (activeItem.value === null || isAtEnd ? firstItem?.key : nextItem?.key) || null
	} else {
		activeItem.value = (activeItem.value === null || isAtStart ? lastItem?.key : prevItem?.key) || null
	}
	centerItem()
}

function nextItem() {
	navigateItem("next")
}

function prevItem() {
	navigateItem("prev")
}

function performAction() {
	const item = filteredFlattenItems.value.find(item => item.key === activeItem.value)
	item && callAction(item.action)
}

function centerItem() {
	const element = document.getElementById(String(activeItem.value || ""))
	element && scrollContent.value && element.scrollIntoView({ block: "nearest" })
}

let abortController: AbortController | null = null

function getPatientsList() {
	abortController?.abort()
	abortController = new AbortController()

	PatientsGroup.value.loading = true

	Api.patients
		.list(
			{
				perPage: search.value ? 10 : 5,
				page: 1,
				filters: { text: search.value }
			},
			abortController.signal
		)
		.then(res => {
			PatientsGroup.value.items = res.data.items.map(patient =>
				createItem(
					patient.id,
					`${patient.name} ${patient.surname}`,
					patient.phone,
					() =>
						routePatients({
							patientId: patient.id,
							patientName: `${patient.name} ${patient.surname}`
						}).navigate(),
					SEARCH_ICONS.patient
				)
			)
		})
		.catch(() => {
			PatientsGroup.value.items = []
		})
		.finally(() => {
			PatientsGroup.value.loading = false
		})
}

watchDebounced(
	search,
	() => {
		getPatientsList()
	},
	{ debounce: 300, maxWait: 5000 }
)

onMounted(() => {
	const isWindows = getOS() === "Windows"

	const keys = useMagicKeys()
	const activeCMD = isWindows ? keys["ctrl+\\"] : keys["cmd+\\"]
	const enterCHR = keys.enter

	useSearchDialog().setup(openBox)

	if (activeCMD) {
		whenever(activeCMD, () => {
			openBox()
		})
	}

	if (enterCHR) {
		whenever(enterCHR, () => {
			if (showSearchBox.value) {
				performAction()
			}
		})
	}
})
</script>

<style lang="scss" scoped>
.search-box-modal {
	.search-box {
		border-radius: 4px;

		.search-input {
			height: 50px;
			gap: 20px;
			padding: 20px;

			input {
				background: transparent;
				outline: none;
				border: none;
				min-width: 100px;
			}

			.n-text--code {
				white-space: nowrap;
			}
		}

		.n-divider {
			margin-top: 0;
			margin-bottom: 0;
		}

		.conten-wrap {
			padding-bottom: 30px;

			.group-empty {
				text-align: center;
				padding: 30px 0 40px 0;
			}
			.group {
				padding: 0 10px;
				.group-title {
					opacity: 0.6;
					margin-bottom: 5px;
					padding: 5px 10px;
					padding-top: 20px;
				}
				.group-list {
					.item {
						padding: 7px 10px;
						gap: 10px;
						cursor: pointer;
						border-radius: 10px;
						width: 100%;
						text-align: left;

						.icon {
							width: 28px;
							height: 28px;
							border-radius: 50%;
							background-color: rgba(var(--primary-color-rgb) / 0.15);
							display: flex;
							justify-content: center;
							align-items: center;
						}
						.title {
							font-weight: bold;
						}
						.label {
							opacity: 0.8;
							font-size: 0.9em;
						}

						&.active {
							background-color: var(--hover-color);
						}
						&:hover {
							box-shadow: 0px 0px 0px 1px var(--primary-color) inset;
						}
					}
				}
			}
		}

		.hint-bar {
			font-size: 12px;
			gap: 20px;
			padding: 10px 0;

			.icon {
				background-color: rgba(255, 255, 255, 0.3);
				width: 18px;
				height: 18px;
				padding-top: 1px;
				text-align: center;
				border-radius: 4px;
				display: flex;
				align-items: center;
				justify-content: center;
			}
			.label {
				opacity: 0.7;
			}
		}
	}
}
</style>
